import type { Run, Source } from './index';
import { runCounts, runError } from './index';

/**
 * One source as GET /api/external-jobs/sources/:id returns it: the service's `job_sources`
 * document, every field the pipeline, the Scout and the status routes write. The list endpoint
 * adds `activeJobs`; the single read does not, so the workspace counts those separately.
 */
export interface SourceDetail extends Omit<Source, 'scout'> {
  companyId?: string | null;
  params?: Record<string, unknown> | null;
  fallbackAdapter?: string | null;
  llmFallbackAllowed?: boolean | null;
  refreshIntervalHours?: number | null;
  countryPolicy?: { allowed?: string[] | null; remote?: boolean | null } | null;
  jobCodeKind?: string | null;
  robotsCheckedAt?: string | null;
  robotsAllowed?: boolean | null;
  termsNote?: string | null;
  legalStatus?: string | null;
  lastSuccessAt?: string | null;
  lastRunId?: string | null;
  consecutiveFailures?: number | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  statusChangedAt?: string | null;
  statusChangedBy?: string | null;
  takedown?: { by?: string | null; at?: string | null; reason?: string | null; purged?: boolean | null } | null;
  nextRunAt?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  notes?: string | null;
  scout?: {
    outcome?: string | null;
    error?: string | null;
    method?: string | null;
    verified?: boolean | null;
    costUsd?: number | null;
    model?: string | null;
    host?: string | null;
    feedback?: string[] | null;
    at?: string | null;
    queuedAt?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
  } | null;
}

/** "Every 24 hours", "Every hour". The service's default is 24 and its floor is one. */
export const refreshLabel = (hours: number | null | undefined): string => {
  const h = Math.max(1, Math.round(typeof hours === 'number' && hours > 0 ? hours : 24));
  return h === 1 ? 'Every hour' : `Every ${h} hours`;
};

/**
 * When the scheduler will next fetch it. Only an ACTIVE source is scheduled; anything else says
 * why not rather than showing a stale date the scheduler will never act on.
 */
export const nextFetchState = (
  s: Pick<SourceDetail, 'status' | 'nextRunAt'>,
): { kind: 'date'; at: string } | { kind: 'text'; text: string } => {
  if (s.status === 'NEEDS_REVIEW') return { kind: 'text', text: 'After approval' };
  if (s.status === 'PAUSED') return { kind: 'text', text: 'Not scheduled while paused' };
  if (s.status === 'DECLINED') return { kind: 'text', text: 'Never, the source is declined' };
  return s.nextRunAt ? { kind: 'date', at: s.nextRunAt } : { kind: 'text', text: 'Due now' };
};

/** robots.txt as the last run recorded it. */
export const robotsLabel = (allowed: boolean | null | undefined): string =>
  allowed === true ? 'Allowed' : allowed === false ? 'Disallowed' : 'Not checked yet';

/** The adapter's own settings as "key: value" lines, nested values as JSON. */
export const paramLines = (params: Record<string, unknown> | null | undefined): string[] =>
  Object.entries(params ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);

export interface SourceEvent {
  id: string;
  kind: 'submitted' | 'status' | 'ai';
  text: string;
  at: string;
  by?: string | null;
}

/**
 * The source's history, newest first: added, scouted, approved or paused or declined, taken down,
 * and each run. The service keeps only the latest status change on the source, so earlier ones
 * are not recoverable here; the runs are the fetch history.
 */
export const sourceEvents = (s: SourceDetail, runs: Run[] = []): SourceEvent[] => {
  const out: SourceEvent[] = [];
  if (s.createdAt) out.push({ id: 'created', kind: 'submitted', text: 'Source added', at: s.createdAt, by: s.createdBy });
  const scoutAt = s.scout?.at || s.scout?.finishedAt;
  if (scoutAt && s.scout?.outcome) {
    out.push({ id: 'scout', kind: 'ai', text: `Scout finished: ${s.scout.outcome.replace(/_/g, ' ')}`, at: scoutAt });
  }
  if (s.approvedAt) out.push({ id: 'approved', kind: 'status', text: 'Approved', at: s.approvedAt, by: s.approvedBy });
  // The latest status change, unless it is the approval already listed.
  if (s.statusChangedAt && s.statusChangedAt !== s.approvedAt && s.status !== 'ACTIVE') {
    out.push({
      id: 'status',
      kind: 'status',
      text: `${s.status === 'PAUSED' ? 'Paused' : s.status === 'DECLINED' ? 'Declined' : 'Moved to review'}${s.declineReason ? `: ${s.declineReason}` : ''}`,
      at: s.statusChangedAt,
      by: s.statusChangedBy,
    });
  }
  if (s.takedown?.at) {
    out.push({
      id: 'takedown',
      kind: 'status',
      text: `Taken down${s.takedown.reason ? `: ${s.takedown.reason}` : ''}`,
      at: s.takedown.at,
      by: s.takedown.by,
    });
  }
  for (const r of runs) {
    if (!r.startedAt) continue;
    const err = runError(r.errors?.[0]);
    out.push({
      id: `run-${r.runId}`,
      kind: 'status',
      text: r.status === 'failed' ? `Run failed${err ? `: ${err}` : ''}` : `Run ${r.status}: ${runCounts(r.counts)}`,
      at: r.startedAt,
      by: r.trigger ?? null,
    });
  }
  return out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
};
